import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { post, put } from 'services/api';
import { PrimaryButton, SecondaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { spacing } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import { useJsonEditorContext } from '../context/JsonEditorContext';
import { useDbConfigContext } from '../context/DbConfigContext';
import { dbConfigUrl } from '../constants';

const ButtonContainer = styled.div`
  display: flex;
  column-gap: ${spacing.base};
`;

export const testIds = {
  btnClean: 'admin-db-configs-clean-btn',
  btnUpdate: 'admin-db-configs-update-btn',
};

export const ActionButtons = () => {
  const dispatchHook = useDispatch();
  const { json, jsonIsDirty, jsonIsValid, name, setLastSavedJson } = useDbConfigContext();
  const { editor } = useJsonEditorContext();
  const cleanIsDisabled = !name || !jsonIsValid || !json;
  const updateIsDisabled = !name || !jsonIsValid || !json || !jsonIsDirty;
  const tooltipContent =
    (!name && 'DB config selection required') ||
    ((!jsonIsValid || !json) && 'JSON must be valid') ||
    (!jsonIsDirty && 'No changes have been made');

  const onClean = () => {
    post(`${dbConfigUrl}/${encodeURIComponent(name)}/clean`, JSON.parse(json))
      .then((config) => {
        // update the editor (result is unsaved, so don't update last saved)
        editor.current?.setValue(JSON.stringify(config));
        // show success toast
        dispatchHook(addPositiveToast(`${name} cleaned successfully. Remember to save changes.`));
      })
      .catch((err) => {
        // show error toast
        dispatchHook(addNegativeToast(`Clean failed (${err.message})`));
      });
  };

  const onUpdate = () => {
    put(`${dbConfigUrl}/${encodeURIComponent(name)}`, JSON.parse(json))
      .then(() => {
        // update last save
        setLastSavedJson(json);
        // show success toast
        dispatchHook(addPositiveToast(`${name} updated successfully`));
      })
      .catch((err) => {
        // show error toast
        dispatchHook(addNegativeToast(`Update failed (${err.message})`));
      });
  };

  return (
    <FormField labelText="Config actions" isHorizontalLayout={true}>
      <ButtonContainer>
        <Tooltip isEnabled={cleanIsDisabled} tooltipContent={tooltipContent}>
          <SecondaryButton
            onClick={onClean}
            isDisabled={cleanIsDisabled}
            data-test={testIds.btnClean}
          >
            Clean
          </SecondaryButton>
        </Tooltip>
        <Tooltip isEnabled={updateIsDisabled} tooltipContent={tooltipContent}>
          <PrimaryButton
            onClick={onUpdate}
            isDisabled={updateIsDisabled}
            data-test={testIds.btnUpdate}
          >
            Update
          </PrimaryButton>
        </Tooltip>
      </ButtonContainer>
    </FormField>
  );
};

export default ActionButtons;
